import { FC, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../Button';
import { openNotification } from '../Notification';
import { downloadFile } from '../../../File';
import { File as GQLFile } from '../../../generated/graphql';

export interface DocumentViewerHeaderProps {
  file: GQLFile;
  downloadURL?: string;
  currentIndex: number;
  totalCount: number;
  onClose: () => void;
  onDownload?: (file: GQLFile) => void;
}

export const DocumentViewerHeader: FC<DocumentViewerHeaderProps> = ({
  file,
  downloadURL,
  currentIndex,
  totalCount,
  onClose,
  onDownload,
}) => {
  const { t: tFile } = useTranslation('file');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    onDownload?.(file);
    setIsDownloading(true);
    try {
      const result = await downloadFile(file.name, downloadURL);
      if (!result.ok) {
        openNotification({ type: 'error', description: tFile('error-unable-to-download') });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-darkMode-gray-light/10 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-lg font-medium text-white">{file.name}</p>
        <p className="text-sm font-normal text-darkMode-gray-light">
          <span className="text-darkMode-gray-light/70">{tFile('date-added-file-label')} </span>
          {format(new Date(file.createdAt), 'MMM d, yyyy')} {tFile('added-by-file-label')} {file.createdBy.givenName}{' '}
          {file.createdBy.familyName}
        </p>
      </div>
      {totalCount > 1 && (
        <p className="shrink-0 text-sm text-darkMode-gray-light">
          {tFile('document-viewer-page-indicator', { current: currentIndex + 1, total: totalCount })}
        </p>
      )}
      <div className="flex shrink-0 items-center gap-2">
        <IconButton
          icon="download"
          theme="text"
          isLoading={isDownloading}
          disabled={!downloadURL}
          aria-label={tFile('download-file-label')}
          onClick={() => void handleDownload()}
        />
        <IconButton icon="close" theme="text" aria-label={tFile('document-viewer-close-label')} onClick={onClose} />
      </div>
    </div>
  );
};
